import { AssetManagementApi } from './asset-management-api';
import {
  AssetListParams,
  AssetListResponse,
  AssetDetailResponse,
  AssetUploadFormData,
  UploadAssetResponse,
  DeleteAssetResponse,
  RollbackAssetResponse,
} from '../types/asset-types';
import {
  transformAssetUrls,
  transformVersionUrls,
} from '../utils/s3-url-utils';

/**
 * Asset Management Repository
 * Provides a clean interface between use cases and the API layer
 */
export class AssetManagementRepository {
  constructor(
    private readonly api: AssetManagementApi,
    private readonly s3BaseUrl: string
  ) {}

  /**
   * Get paginated list of assets with optional filters
   */
  async getAssets(params: AssetListParams): Promise<AssetListResponse> {
    const response = await this.api.getAssets(params);

    // Transform all asset URLs if successful
    if (response.isSuccess && response.items && response.items.length > 0) {
      return {
        ...response,
        items: response.items.map(asset => transformAssetUrls(asset, this.s3BaseUrl))
      };
    }

    return response;
  }

  /**
   * Get asset detail with version history
   */
  async getAssetDetail(assetId: number): Promise<AssetDetailResponse> {
    const response = await this.api.getAssetDetail(assetId);

    // Transform asset and version URLs if successful
    if (response.isSuccess && response.asset) {
      return {
        ...response,
        asset: transformAssetUrls(response.asset, this.s3BaseUrl),
        versions: (response.versions ?? []).map(v => transformVersionUrls(v, this.s3BaseUrl)),
      };
    }

    return response;
  }

  /**
   * Upload a new asset or create a new version of existing asset
   * Note: operatorId is automatically injected by the API layer from user context
   * if not provided, or uses the provided value for distributors
   */
  async uploadAsset(request: AssetUploadFormData): Promise<UploadAssetResponse> {
    const response = await this.api.uploadAsset(request);

    // Transform uploaded asset and version URLs if successful
    if (response.isSuccess && response.asset) {
      return {
        ...response,
        asset: transformAssetUrls(response.asset, this.s3BaseUrl),
        version: response.version
          ? transformVersionUrls(response.version, this.s3BaseUrl)
          : undefined
      };
    }

    return response;
  }

  /**
   * Delete an asset and all its versions
   */
  async deleteAsset(assetId: number): Promise<DeleteAssetResponse> {
    return this.api.deleteAsset(assetId);
  }

  /**
   * Rollback an asset to a previous version
   */
  async rollbackAsset(assetId: number, versionId: number): Promise<RollbackAssetResponse> {
    const response = await this.api.rollbackAsset(assetId, versionId);

    // Transform rolled-back asset and version URLs if successful
    if (response.isSuccess && response.asset) {
      return {
        ...response,
        asset: transformAssetUrls(response.asset, this.s3BaseUrl),
        version: response.version
          ? transformVersionUrls(response.version, this.s3BaseUrl)
          : undefined
      };
    }

    return response;
  }
}
